import { BOUNDARY_POLICY_PREFIX } from '../boundary/boundary.js';
/**
 * What `QrveyDeployExecRole` may do, when the access stack is deployed with
 * `DeployPolicy=scoped` (IM-50).
 *
 * `AdministratorAccess` was the interim since IM-24, and the measurement that
 * was meant to replace it (IM-128, `docs/run-permissions-measurement.md`) is
 * 245 actions across 23 services. This is that measurement turned into a
 * policy, on the design of the reviewed 24/7 Software production set — with
 * one change that set could not make: 24/7 hard-coded two deployment ids,
 * because it was written for one customer, and this is written for every
 * customer at once. So every scoped statement names **the session's own
 * deployment id**, `${aws:PrincipalTag/qrvey-id}`, which the runner wrapper
 * sets as a session tag when it assumes the role. A run can therefore reach
 * its own deployment and nothing else: not the customer's resources, and not
 * another Qrvey instance in the same account.
 *
 * ## How the statements decide what is "this deployment's"
 *
 * Three ways, in order of strength, and each resource type uses the strongest
 * one it supports:
 *
 * - **Its `qrvey-id` tag equals the session's.** Exact, for every resource the
 *   installer tags — and it tags nearly everything, through the provider's
 *   `default_tags` (`main.tf` `local.platform_tags`). EC2, load balancers,
 *   KMS keys, RDS proxies, CloudFront distributions, event rules, IAM roles and
 *   policies.
 * - **Its name carries the id at a delimiter.** `qrvey-eks-<id>`,
 *   `qrvey-<id>-*`, `<id>-dataload-*`. Delimited rather than `*<id>*`, which
 *   is what 24/7 used: `*abc*` also matches deployment `abc2`'s resources, and
 *   delimited patterns do not.
 * - **Its name carries no id at all.** `KarpenterController-*`,
 *   `karpenter-eks-node-group-*`, Karpenter's `Karpenter*` event rules and the
 *   CloudFront origin access identities. These are the residual risk, and each
 *   is also held to the tag wherever the service supports one.
 *
 * ## Why tags cannot simply be re-applied
 *
 * Tag-scoping is only as strong as the rule about who may set the tag. Without
 * a guard, tag-then-delete defeats every statement below: put your own id on
 * the customer's load balancer, then delete it. `QrveyDeploy-Guardrails`
 * therefore refuses, explicitly:
 *
 * - any session that carries no deployment tag at all (fails closed on an old
 *   dispatcher, rather than open);
 * - any request that sets `qrvey-id` to another deployment's value, in any
 *   service;
 * - re-tagging a resource that already carries another deployment's id;
 * - adding `qrvey-id` to an **existing** EC2 resource, which EC2 can tell apart
 *   from tag-on-create through `ec2:CreateAction`.
 *
 * What it cannot refuse is the one gap IAM leaves: outside EC2 there is no
 * condition that tells "tagging the resource this request is creating" from
 * "tagging an existing resource that has no tag yet", so the few tag-scoped
 * non-EC2 types (KMS keys, RDS proxies, CloudFront distributions, OIDC
 * providers, and the IAM names that carry no id) can be claimed by a run if
 * they exist untagged. `docs/deploy-permissions.md` lists them.
 *
 * ## The IAM half, which is still the interim
 *
 * The installer creates ten IAM roles at the root path, two with no deployment
 * id in their names, and none with a permissions boundary (IM-128). So role
 * creation is scoped by name pattern and by tag, attachment is limited to an
 * allow-list of policy ARNs, `PassRole` to four services — and `PutRolePolicy`
 * still writes an inline policy nobody can constrain by condition. A boundary
 * (MP-3619) is the only thing that closes that, which is why the access
 * template takes an optional `DeployRoleBoundaryArn` that, once the image sets
 * one, makes it mandatory here.
 *
 * From bootstrap v10 (IM-193) a run may set a boundary on its own roles, and
 * only its own deployment's — `qrvey-boundary-<id>`, which the customer
 * creates and owns (IM-192). It may never remove one, set another, or write to
 * any `qrvey-boundary-*` policy, each refused explicitly in
 * `QrveyDeploy-IamGuardrails`. Requiring one on every role it creates is the
 * account-wide `required` mode, IM-194.
 *
 * ## Form
 *
 * Authored in IAM's own syntax with two CloudFormation tokens,
 * `${AWS::Partition}` and `${AWS::AccountId}`. `forCloudFormation` wraps every
 * string holding a token in `Fn::Sub` and escapes the IAM variable as
 * `${!aws:PrincipalTag/qrvey-id}` so `Fn::Sub` does not eat it;
 * `forAccount` substitutes the tokens for a real account, which is what the
 * size test, the simulator script and the customer document read.
 */
/** The session tag every scoped statement is pinned to. */
export const DEPLOYMENT_TAG_KEY = 'qrvey-id';
/** How IAM names the session's own deployment id. Literal in the rendered policy. */
export const SESSION_DEPLOYMENT_ID = '${aws:PrincipalTag/qrvey-id}';
/**
 * The shape the runner wrapper requires of a deployment id before it tags a
 * session with one. The same rule as `deploymentIdProblem` — eighteen
 * lowercase letters and digits — restated as a pattern for the shell.
 */
export const SESSION_TAG_VALUE_PATTERN = '^[a-z0-9]{1,18}$';
/** Names of the managed policies, so the guard can protect them from the run. */
export const DEPLOY_POLICY_NAME_PREFIX = 'QrveyDeploy-';
const P = '${AWS::Partition}';
const A = '${AWS::AccountId}';
const ID = SESSION_DEPLOYMENT_ID;
const CLUSTER = `qrvey-eks-${ID}`;
/** A regional ARN in this account, any region: the grant covers every region. */
const arn = (service, rest) => `arn:${P}:${service}:*:${A}:${rest}`;
const iam = (rest) => `arn:${P}:iam::${A}:${rest}`;
const s3 = (rest) => `arn:${P}:s3:::${rest}`;
/** The resource carries this session's own deployment id. */
const OWN = { StringEquals: { 'aws:ResourceTag/qrvey-id': ID } };
/** The request tags what it creates with this session's own deployment id. */
const TAGGED = { StringEquals: { 'aws:RequestTag/qrvey-id': ID } };
/**
 * Tags that mark a resource as this deployment's without carrying `qrvey-id`.
 *
 * Each is written by something inside this deployment's own cluster, naming the
 * cluster, and none can be written by this run on a resource that is not
 * already its own — the EC2 tagging statements below allow tag changes only on
 * create or on resources already this deployment's. `aws:eks:cluster-name` is
 * reserved to AWS outright.
 *
 * - `aws:eks:cluster-name` — EKS, on the cluster security group it creates.
 * - `cluster.k8s.amazonaws.com/name` — the VPC CNI, on the network interfaces
 *   it attaches to nodes. The destroy deletes the ones left `available`, and
 *   they carry no `qrvey-id`.
 * - `elbv2.k8s.aws/cluster` — the AWS Load Balancer Controller, on the load
 *   balancers, target groups and security groups it creates after a GitOps
 *   adoption. The destroy matches its security groups by exactly this tag.
 * - `ebs.csi.aws.com/cluster-name` — the EBS CSI driver, on the persistent
 *   volumes it provisions (RabbitMQ, Prometheus, Loki, Alertmanager). The
 *   first scoped destroy (2026-09-29) was refused deleting four of them: they
 *   carry no `qrvey-id` whatever the storage class says.
 *
 * Karpenter's nodes carry `aws:eks:cluster-name` too — measured on the same
 * destroy, where a RabbitMQ node without `qrvey-id` had to be terminated.
 */
const CONTROLLER_SIGNALS = {
    'aws:ResourceTag/aws:eks:cluster-name': CLUSTER,
    'aws:ResourceTag/cluster.k8s.amazonaws.com/name': CLUSTER,
    'aws:ResourceTag/elbv2.k8s.aws/cluster': CLUSTER,
    'aws:ResourceTag/ebs.csi.aws.com/cluster-name': CLUSTER,
};
/**
 * Destructive and networking actions refused on anything that is not this
 * deployment's (`DenyOutsideThisDeployment`).
 *
 * 24/7's list, extended with every delete the measured destroy made in EC2, the
 * load balancers and Auto Scaling. Only actions whose resources all support
 * `aws:ResourceTag` belong here: the deny is a `StringNotEquals`, which also
 * matches when the tag is absent — that is the point, an untagged resource is
 * refused — so an action on a resource type that cannot carry the key would be
 * refused always. That is why no *create* is in this list: the resource being
 * created has no tags yet.
 */
/**
 * Network ACL entries, refused outside this deployment by their own two denies
 * rather than by the rail (`DenyNaclEntriesOfOtherDeployments`,
 * `DenyNaclEntriesOfNamedAcls`).
 *
 * The rail refuses anything without this deployment's tag, and the VPC's
 * **default** network ACL has none when its entries change: the provider's
 * `aws_default_network_acl` rewrites the entries first and tags the ACL after,
 * the reverse of the default security group and route table, which are tagged
 * before their rules move. Measured on the first scoped apply (2026-09-29):
 * `DeleteNetworkAclEntry` on the new VPC's untagged default ACL, explicitly
 * denied, on all four of the installer's attempts.
 *
 * So an ACL carrying **neither** `qrvey-id` nor `Name` may have its entries
 * changed. That adds no reach: `Ec2TagAdoptedVpcDefaults` already lets a run
 * claim exactly such an ACL by tagging it, and then change it as its own.
 * Another deployment's ACL, and any ACL someone has named, stay refused.
 */
export const NACL_ENTRY_ACTIONS = [
    'ec2:CreateNetworkAclEntry',
    'ec2:ReplaceNetworkAclEntry',
    'ec2:DeleteNetworkAclEntry',
];
export const DENY_OUTSIDE_DEPLOYMENT = [
    'ec2:TerminateInstances',
    'ec2:StopInstances',
    'ec2:ModifyInstanceAttribute',
    'ec2:DeleteVolume',
    'ec2:DetachVolume',
    'ec2:DeleteNetworkInterface',
    'ec2:ModifyNetworkInterfaceAttribute',
    'ec2:DeleteLaunchTemplate',
    'ec2:DeleteLaunchTemplateVersions',
    'ec2:ModifyLaunchTemplate',
    'ec2:DeleteSecurityGroup',
    'ec2:AuthorizeSecurityGroupIngress',
    'ec2:AuthorizeSecurityGroupEgress',
    'ec2:RevokeSecurityGroupIngress',
    'ec2:RevokeSecurityGroupEgress',
    'ec2:ModifySecurityGroupRules',
    'ec2:DeleteNatGateway',
    'ec2:CreateRoute',
    'ec2:ReplaceRoute',
    'ec2:DeleteRoute',
    'ec2:DeleteRouteTable',
    'ec2:AssociateRouteTable',
    'ec2:DisassociateRouteTable',
    'ec2:ReplaceRouteTableAssociation',
    'ec2:DeleteSubnet',
    'ec2:ModifySubnetAttribute',
    'ec2:DeleteVpc',
    'ec2:ModifyVpcAttribute',
    'ec2:DeleteVpcEndpoints',
    'ec2:ModifyVpcEndpoint',
    'ec2:ReleaseAddress',
    'ec2:DisassociateAddress',
    'ec2:AttachInternetGateway',
    'ec2:DetachInternetGateway',
    'ec2:DeleteInternetGateway',
    'elasticloadbalancing:DeleteLoadBalancer',
    'elasticloadbalancing:DeleteTargetGroup',
    'elasticloadbalancing:DeleteListener',
    'elasticloadbalancing:DeleteRule',
    'elasticloadbalancing:ModifyLoadBalancerAttributes',
    'elasticloadbalancing:ModifyListener',
    'elasticloadbalancing:ModifyTargetGroup',
    'elasticloadbalancing:DeregisterTargets',
    'elasticloadbalancing:SetSecurityGroups',
    'elasticloadbalancing:SetSubnets',
    'autoscaling:DeleteAutoScalingGroup',
    'autoscaling:UpdateAutoScalingGroup',
    'autoscaling:SetDesiredCapacity',
    'autoscaling:TerminateInstanceInAutoScalingGroup',
    'autoscaling:DetachInstances',
    'autoscaling:SuspendProcesses',
];
/**
 * Every tagging API the policy lets a run call, in any service.
 *
 * `DenyRetaggingAnotherDeployment` refuses each of them on a resource that
 * already carries another deployment's id — so no run can take another
 * instance's resources by re-tagging them, whatever else it may tag.
 */
export const TAGGING_ACTIONS = [
    'ec2:CreateTags',
    'ec2:DeleteTags',
    'elasticloadbalancing:AddTags',
    'elasticloadbalancing:RemoveTags',
    'autoscaling:CreateOrUpdateTags',
    'autoscaling:DeleteTags',
    'eks:TagResource',
    'eks:UntagResource',
    'rds:AddTagsToResource',
    'rds:RemoveTagsFromResource',
    'es:AddTags',
    'es:RemoveTags',
    'kms:TagResource',
    'kms:UntagResource',
    'secretsmanager:TagResource',
    'secretsmanager:UntagResource',
    'logs:TagResource',
    'logs:UntagResource',
    'logs:TagLogGroup',
    'logs:UntagLogGroup',
    'sns:TagResource',
    'sns:UntagResource',
    'sqs:TagQueue',
    'sqs:UntagQueue',
    'events:TagResource',
    'events:UntagResource',
    'athena:TagResource',
    'athena:UntagResource',
    'glue:TagResource',
    'glue:UntagResource',
    'cloudfront:TagResource',
    'cloudfront:UntagResource',
    'ssm:AddTagsToResource',
    'ssm:RemoveTagsFromResource',
    'iam:TagRole',
    'iam:UntagRole',
    'iam:TagPolicy',
    'iam:UntagPolicy',
    'iam:TagOpenIDConnectProvider',
    'iam:UntagOpenIDConnectProvider',
    'iam:TagInstanceProfile',
    'iam:UntagInstanceProfile',
    'geo:TagResource',
    'geo:UntagResource',
];
/**
 * The IAM path the installer creates its roles, policies and Karpenter's
 * instance profile under, from the QRV-1135 image on (MP-3619).
 *
 * A deployment created by that image takes it; one that existed before keeps
 * `/` for its life, because moving a role's path replaces the EKS cluster. So
 * both forms are allowed, and an account can hold deployments on each.
 *
 * Inside it the writes are held to the tag rather than the name: the path is
 * the installer's own mark, every role and policy in it carries the
 * `qrvey-id` of the deployment that made it, and `DenyRetaggingAnotherDeployment`
 * stops one run claiming another's. `PassRole` is the exception, because IAM
 * does not evaluate it against a role's tags, so it keeps the id-bearing names
 * under this path as it does at the root.
 */
export const INSTALLER_IAM_PATH = '/qrvey/';
const UNDER_PATH = INSTALLER_IAM_PATH.slice(1);
/**
 * The IAM role names the installer creates (IM-128), as ARN patterns.
 *
 * Every write to one of these also requires the role to carry this session's
 * `qrvey-id`, so a pattern is a necessary condition and never a sufficient one.
 * The last two carry no deployment id at all and are the IAM residual: see the
 * module comment.
 */
export const ROLE_PATTERNS = [
    iam(`role/qrvey-eks-${ID}-*`),
    iam(`role/${ID}-*`),
    iam(`role/${ID}_*`),
    iam(`role/eks-pod-identity-${ID}`),
    iam(`role/eks-migration-tool-${ID}`),
    iam(`role/Karpenter-qrvey-eks-${ID}-*`),
    iam(`role/qrvey-rds-proxy-${ID}`),
    iam(`role/qrvey-rds-proxy-${ID}-*`),
    // Karpenter's controller as MP-3693 names it for a new deployment. Its node
    // role, `<id>-karpenter-node-group-*`, is already `${ID}-*` above.
    iam(`role/KarpenterController-${ID}-*`),
    // No deployment id: the names a deployment created before MP-3693 keeps for
    // its life. Allowed by prefix, held to the tag. Residual risk.
    iam('role/KarpenterController-*'),
    iam('role/karpenter-eks-node-group-*'),
];
/** The customer-managed policies the installer creates, as ARN patterns. */
export const POLICY_PATTERNS = [
    iam(`policy/qrvey-eks-${ID}-*`),
    iam(`policy/${ID}-*`),
    iam(`policy/${ID}_*`),
    // Karpenter's controller policy. No deployment id; held to the tag.
    iam('policy/KarpenterController-*'),
];
/** Moves a root-path IAM pattern under the installer's path. */
const underPath = (pattern, kind) => pattern.replace(`:${kind}/`, `:${kind}/${UNDER_PATH}`);
/** Every role under the installer's path. Written only while it carries this deployment's tag. */
export const PATH_ROLES = iam(`role/${UNDER_PATH}*`);
/** Every policy under the installer's path, on the same terms. */
export const PATH_POLICIES = iam(`policy/${UNDER_PATH}*`);
/** The role names `PassRole` may name under the path: the root ones, moved, since it ignores tags. */
export const PATH_ROLE_PATTERNS = ROLE_PATTERNS.map((p) => underPath(p, 'role'));
/** This deployment's own policies under the path, which alone may be attached from there. */
export const PATH_POLICY_PATTERNS = POLICY_PATTERNS.map((p) => underPath(p, 'policy'));
/**
 * The AWS-managed policies the installer attaches, and nothing else.
 *
 * Read off the image (`9.5-20260924-2136`: the root module, `eks` 20.37.2 and
 * its node group and Karpenter submodules, `rds-aurora` 9.13.0). This is what
 * stops the obvious escalation — create a role, attach `AdministratorAccess`,
 * pass it on — which a name pattern alone would not. `AmazonS3FullAccess` is on
 * the list because the installer attaches it to a pod role
 * (`<id>_dataload_DbDatasourceExportToS3Role_MP`, `k8s-base.tf`); that is the
 * image's decision and an MP finding, not a permission this role uses itself.
 */
export const ATTACHABLE_AWS_POLICIES = [
    'AmazonEKSClusterPolicy',
    'AmazonEKSVPCResourceController',
    'AmazonEKSWorkerNodePolicy',
    'AmazonEKS_CNI_Policy',
    'AmazonEC2ContainerRegistryReadOnly',
    'AmazonSSMManagedInstanceCore',
    'AmazonS3FullAccess',
    'AmazonAthenaFullAccess',
    'service-role/AmazonRDSEnhancedMonitoringRole',
];
/**
 * Services a role may be passed to (`iam:PassedToService`).
 *
 * `eks` for the cluster and node group roles, `pods.eks` for pod identity
 * associations — the three passes the measurement names. `ec2` because Amit's
 * decision names it and 24/7 carries it, though no measured call needed it:
 * the deploy role launches no instance itself. `rds` because `CreateDBProxy`
 * takes the proxy's role (`qrvey-rds-proxy-<id>`) — a pass the measurement's
 * table missed, since `PassRole` is an authorization check CloudTrail never
 * records.
 */
export const PASSED_TO_SERVICES = [
    'eks.amazonaws.com',
    'pods.eks.amazonaws.com',
    'ec2.amazonaws.com',
    'rds.amazonaws.com',
];
/**
 * Where a service decrypts on the run's behalf (`kms:ViaService`).
 *
 * 24/7's list plus `eks` (cluster secret encryption) and `ssm` (SecureString
 * parameters the installer writes). A decrypt through one of these still needs
 * that service's own permission on the resource, and those are scoped — so this
 * does not reach the customer's encrypted data.
 */
const VIA_SERVICES = [
    'rds',
    'ec2',
    'elasticfilesystem',
    'es',
    's3',
    'secretsmanager',
    'logs',
    'sns',
    'sqs',
    'eks',
    'ssm',
].map((service) => `${service}.*.amazonaws.com`);
/**
 * The permissions boundaries the customer creates, one per deployment, at the
 * root path whatever the deployment's own path (IM-192, MP-3693).
 *
 * The customer owns them: the installer only sets one on a role, and the run
 * may never write one, or a run could rewrite the limit it is held to.
 */
export { BOUNDARY_POLICY_PREFIX };
export const BOUNDARY_POLICIES = iam(`policy/${BOUNDARY_POLICY_PREFIX}*`);
/** This deployment's own boundary, the only one a run may set. */
export const OWN_BOUNDARY = iam(`policy/${BOUNDARY_POLICY_PREFIX}${ID}`);
/** Every policy ARN a role may have attached: the installer's own, and the list above. */
const ATTACHABLE_POLICY_ARNS = [
    ...POLICY_PATTERNS,
    // This deployment's own, by name — never `policy/qrvey/*`, which would let a
    // run attach another deployment's policy to its own role.
    ...PATH_POLICY_PATTERNS,
    ...ATTACHABLE_AWS_POLICIES.map((name) => `arn:${P}:iam::aws:policy/${name}`),
];
const guardrails = {
    name: `${DEPLOY_POLICY_NAME_PREFIX}Guardrails`,
    description: 'Qrvey deploy role: the explicit refusals every other statement relies on.',
    statements: [
        {
            /*
             * A session with no deployment tag gets nothing at all.
             *
             * Every scoped statement compares against `${aws:PrincipalTag/qrvey-id}`,
             * and a variable that does not resolve makes a condition fail — which
             * for an Allow is a refusal, but for a Deny is the deny *not applying*.
             * So an untagged session would slip under the rails below. This is the
             * statement that turns that into a refusal, loudly: an older dispatcher,
             * whose wrapper does not tag, fails on its first call instead of running
             * with guards that do not engage.
             */
            Sid: 'DenyWithoutDeploymentTag',
            Effect: 'Deny',
            NotAction: ['sts:GetCallerIdentity'],
            Resource: '*',
            Condition: { Null: { 'aws:PrincipalTag/qrvey-id': 'true' } },
        },
        {
            // Nothing may be created, or tagged, as belonging to another deployment.
            Sid: 'DenyOtherDeploymentsTag',
            Effect: 'Deny',
            Action: '*',
            Resource: '*',
            Condition: {
                Null: { 'aws:RequestTag/qrvey-id': 'false' },
                StringNotEquals: { 'aws:RequestTag/qrvey-id': ID },
            },
        },
        {
            // Nor may another deployment's resources be re-tagged in any way.
            Sid: 'DenyRetaggingAnotherDeployment',
            Effect: 'Deny',
            Action: [...TAGGING_ACTIONS],
            Resource: '*',
            Condition: {
                Null: { 'aws:ResourceTag/qrvey-id': 'false' },
                StringNotEquals: { 'aws:ResourceTag/qrvey-id': ID },
            },
        },
        {
            /*
             * `qrvey-id` is set when an EC2 resource is created, and never after.
             *
             * Two exceptions, each for a resource that is this deployment's without
             * having been created by this role: the cluster security group, which
             * EKS creates and marks with the reserved `aws:eks:cluster-name`, and
             * which the EKS module then tags (`aws_ec2_tag.cluster_primary_security_group`);
             * and a resource already carrying this deployment's id.
             *
             * Security groups, route tables and network ACLs are the next statement,
             * because they need one more exception.
             */
            Sid: 'DenyDeploymentTagOnExistingEc2',
            Effect: 'Deny',
            Action: ['ec2:CreateTags', 'ec2:DeleteTags'],
            NotResource: [
                arn('ec2', 'security-group/*'),
                arn('ec2', 'route-table/*'),
                arn('ec2', 'network-acl/*'),
            ],
            Condition: {
                Null: { 'ec2:CreateAction': 'true' },
                'ForAnyValue:StringEquals': { 'aws:TagKeys': ['qrvey-id'] },
                StringNotEquals: {
                    'aws:ResourceTag/qrvey-id': ID,
                    'aws:ResourceTag/aws:eks:cluster-name': CLUSTER,
                },
            },
        },
        {
            /*
             * The same, for the three types the VPC module adopts.
             *
             * `terraform-aws-modules/vpc` 5.16.0 manages the VPC's *default* security
             * group, route table and network ACL, which AWS creates with the VPC and
             * untagged — so Terraform tags them afterwards, `qrvey-id` included, on
             * every fresh apply. IAM has no condition that says "this is a VPC's
             * default", and refusing it fails the apply. So these three types may be
             * given the tag while they carry **no `Name` tag**, which the defaults do
             * not and a customer's own groups and tables nearly always do. That is a
             * narrowing, not a boundary, and it is listed as residual risk. The fix is
             * the image's: `manage_default_security_group`, `manage_default_route_table`
             * and `manage_default_network_acl` set to false.
             */
            Sid: 'DenyDeploymentTagOnNamedNetworkResources',
            Effect: 'Deny',
            Action: ['ec2:CreateTags', 'ec2:DeleteTags'],
            Resource: [
                arn('ec2', 'security-group/*'),
                arn('ec2', 'route-table/*'),
                arn('ec2', 'network-acl/*'),
            ],
            Condition: {
                Null: { 'ec2:CreateAction': 'true', 'aws:ResourceTag/Name': 'false' },
                'ForAnyValue:StringEquals': { 'aws:TagKeys': ['qrvey-id'] },
                StringNotEquals: {
                    'aws:ResourceTag/qrvey-id': ID,
                    'aws:ResourceTag/aws:eks:cluster-name': CLUSTER,
                },
            },
        },
        {
            /*
             * The rail. Destructive and networking actions on anything that is not
             * this deployment's are refused, whatever an Allow says.
             *
             * `StringNotEquals` over four keys in one block is an AND: the deny
             * applies only when the resource carries none of this deployment's
             * marks. A missing key counts as "not equal", so an untagged resource is
             * refused — which the simulator is asked to confirm
             * (`scripts/verify-deploy-scope.sh`).
             */
            Sid: 'DenyNaclEntriesOfOtherDeployments',
            Effect: 'Deny',
            Action: [...NACL_ENTRY_ACTIONS],
            Resource: '*',
            Condition: {
                Null: { 'aws:ResourceTag/qrvey-id': 'false' },
                StringNotEquals: { 'aws:ResourceTag/qrvey-id': ID },
            },
        },
        {
            // A named ACL that is not this deployment's: someone chose it, so no run may.
            Sid: 'DenyNaclEntriesOfNamedAcls',
            Effect: 'Deny',
            Action: [...NACL_ENTRY_ACTIONS],
            Resource: '*',
            Condition: {
                Null: { 'aws:ResourceTag/Name': 'false' },
                StringNotEquals: { 'aws:ResourceTag/qrvey-id': ID },
            },
        },
        {
            Sid: 'DenyOutsideThisDeployment',
            Effect: 'Deny',
            Action: [...DENY_OUTSIDE_DEPLOYMENT],
            Resource: '*',
            Condition: {
                StringNotEquals: { 'aws:ResourceTag/qrvey-id': ID, ...CONTROLLER_SIGNALS },
            },
        },
    ],
};
/**
 * The IAM refusals, apart from the other rails only for size.
 *
 * Each restates, as an explicit deny, a limit the IAM allows already draw — so
 * the limit survives the day an allow is widened, which is the same reason the
 * diagnostics role denies what its allow list already omits. And each is what
 * `scripts/verify-deploy-scope.sh` can then show as `explicitDeny` rather than
 * the weaker "no statement allows it".
 */
const iamGuardrails = {
    name: `${DEPLOY_POLICY_NAME_PREFIX}IamGuardrails`,
    description: 'Qrvey deploy role: the IAM refusals no allow may widen.',
    statements: [
        {
            /*
             * The stack that grants this role, the roles either side of it, and the
             * customer's deployment boundaries (IM-193) are not the run's to change:
             * no version, default version, delete or tag. No Allow reaches them
             * today; this keeps it so when an Allow widens.
             */
            Sid: 'DenyChangingQrveyAccess',
            Effect: 'Deny',
            Action: [
                'iam:Add*',
                'iam:Attach*',
                'iam:Create*',
                'iam:Delete*',
                'iam:Detach*',
                'iam:PassRole',
                'iam:Put*',
                'iam:Remove*',
                'iam:Set*',
                'iam:Tag*',
                'iam:Untag*',
                'iam:Update*',
            ],
            Resource: [
                iam('role/Qrvey*'),
                iam(`policy/${DEPLOY_POLICY_NAME_PREFIX}*`),
                BOUNDARY_POLICIES,
            ],
        },
        {
            /*
             * A boundary, once set, stays. Removing one frees the role from the
             * limit the customer chose for it — on any role, this deployment's
             * included, since the run would be lifting its own ceiling.
             */
            Sid: 'DenyRemovingBoundaries',
            Effect: 'Deny',
            Action: 'iam:DeleteRolePermissionsBoundary',
            Resource: '*',
        },
        {
            /*
             * Nor swapped. Setting any boundary but this deployment's own would
             * replace a narrow one with a wide one, or with another deployment's.
             */
            Sid: 'DenyOtherBoundaries',
            Effect: 'Deny',
            Action: 'iam:PutRolePermissionsBoundary',
            Resource: '*',
            Condition: { StringNotEquals: { 'iam:PermissionsBoundary': OWN_BOUNDARY } },
        },
        {
            // No role outside the installer's own names is created, changed or passed.
            Sid: 'DenyRolesOutsideInstallerNames',
            Effect: 'Deny',
            Action: [
                'iam:CreateRole',
                'iam:AttachRolePolicy',
                'iam:PutRolePolicy',
                'iam:PutRolePermissionsBoundary',
                'iam:PassRole',
                'iam:UpdateAssumeRolePolicy',
            ],
            NotResource: [...ROLE_PATTERNS, PATH_ROLES],
        },
        {
            Sid: 'DenyPassingToOtherServices',
            Effect: 'Deny',
            Action: 'iam:PassRole',
            Resource: '*',
            Condition: { StringNotEquals: { 'iam:PassedToService': [...PASSED_TO_SERVICES] } },
        },
        {
            // AdministratorAccess, or anything else not on the list, never attaches.
            Sid: 'DenyAttachingOtherPolicies',
            Effect: 'Deny',
            Action: 'iam:AttachRolePolicy',
            Resource: '*',
            Condition: { ArnNotLike: { 'iam:PolicyARN': ATTACHABLE_POLICY_ARNS } },
        },
    ],
};
const compute = {
    name: `${DEPLOY_POLICY_NAME_PREFIX}Compute`,
    description: 'Qrvey deploy role: the network, EC2, load balancers and Auto Scaling.',
    statements: [
        { Sid: 'Ec2Read', Effect: 'Allow', Action: 'ec2:Describe*', Resource: '*' },
        {
            // Creates that bring nothing existing with them. The tag is the scope.
            Sid: 'Ec2CreateTagged',
            Effect: 'Allow',
            Action: [
                'ec2:CreateVpc',
                'ec2:CreateInternetGateway',
                'ec2:AllocateAddress',
                'ec2:CreateLaunchTemplate',
            ],
            Resource: '*',
            Condition: TAGGED,
        },
        {
            /*
             * Creates that land inside an existing network: the new resource must
             * carry the tag, and `Ec2CreateReferencesOwn` requires what it lands in
             * to be this deployment's too.
             *
             * IAM checks every resource of a request separately, against every
             * statement — so a statement may list only the types its actions
             * *create*, never a type one of them merely references. These three
             * reference nothing but their VPC.
             */
            Sid: 'Ec2CreateNetworkTagged',
            Effect: 'Allow',
            Action: ['ec2:CreateSubnet', 'ec2:CreateRouteTable', 'ec2:CreateSecurityGroup'],
            Resource: [
                arn('ec2', 'subnet/*'),
                arn('ec2', 'route-table/*'),
                arn('ec2', 'security-group/*'),
            ],
            Condition: TAGGED,
        },
        {
            // And these reference subnets, security groups and route tables, so
            // those types are deliberately absent here.
            Sid: 'Ec2CreateAttachedTagged',
            Effect: 'Allow',
            Action: ['ec2:CreateNatGateway', 'ec2:CreateVpcEndpoint', 'ec2:RunInstances'],
            Resource: [
                arn('ec2', 'natgateway/*'),
                arn('ec2', 'vpc-endpoint/*'),
                arn('ec2', 'instance/*'),
                arn('ec2', 'volume/*'),
                arn('ec2', 'network-interface/*'),
            ],
            Condition: TAGGED,
        },
        {
            Sid: 'Ec2CreateReferencesOwn',
            Effect: 'Allow',
            Action: [
                'ec2:CreateSubnet',
                'ec2:CreateRouteTable',
                'ec2:CreateSecurityGroup',
                'ec2:CreateNatGateway',
                'ec2:CreateVpcEndpoint',
                'ec2:RunInstances',
            ],
            Resource: [
                arn('ec2', 'vpc/*'),
                arn('ec2', 'subnet/*'),
                arn('ec2', 'route-table/*'),
                arn('ec2', 'security-group/*'),
                arn('ec2', 'elastic-ip/*'),
                arn('ec2', 'launch-template/*'),
            ],
            Condition: OWN,
        },
        {
            // What an instance is launched from. Only a dry run was measured.
            Sid: 'Ec2LaunchSources',
            Effect: 'Allow',
            Action: 'ec2:RunInstances',
            Resource: [`arn:${P}:ec2:*::image/*`, `arn:${P}:ec2:*::snapshot/*`, arn('ec2', 'key-pair/*')],
        },
        {
            // Changes and deletes, on this deployment's own EC2 resources only.
            Sid: 'Ec2ManageOwn',
            Effect: 'Allow',
            Action: [
                'ec2:AttachInternetGateway',
                'ec2:AssociateRouteTable',
                'ec2:CreateRoute',
                'ec2:CreateNetworkAclEntry',
                'ec2:DeleteNetworkAclEntry',
                'ec2:ModifySubnetAttribute',
                'ec2:ModifyVpcAttribute',
                'ec2:AuthorizeSecurityGroupIngress',
                'ec2:AuthorizeSecurityGroupEgress',
                'ec2:RevokeSecurityGroupIngress',
                'ec2:RevokeSecurityGroupEgress',
                'ec2:CreateLaunchTemplateVersion',
                'ec2:ModifyLaunchTemplate',
                'ec2:DeleteLaunchTemplateVersions',
                'ec2:DeleteLaunchTemplate',
                'ec2:TerminateInstances',
                'ec2:DeleteVolume',
                'ec2:DeleteNetworkInterface',
                'ec2:DeleteSecurityGroup',
                'ec2:DeleteNatGateway',
                'ec2:ReleaseAddress',
                'ec2:DeleteRoute',
                'ec2:DisassociateRouteTable',
                'ec2:DeleteRouteTable',
                'ec2:DeleteVpcEndpoints',
                'ec2:DetachInternetGateway',
                'ec2:DeleteInternetGateway',
                'ec2:DeleteSubnet',
                'ec2:DeleteVpc',
            ],
            Resource: '*',
            Condition: OWN,
        },
        {
            /*
             * Tags on create, whatever the create. Which resource may be created is
             * the create's own statement; this only lets it arrive tagged.
             */
            Sid: 'Ec2TagOnCreate',
            Effect: 'Allow',
            Action: 'ec2:CreateTags',
            Resource: '*',
            Condition: { Null: { 'ec2:CreateAction': 'false' } },
        },
        {
            // Terraform's later tag edits, on resources already this deployment's.
            Sid: 'Ec2RetagOwn',
            Effect: 'Allow',
            Action: ['ec2:CreateTags', 'ec2:DeleteTags'],
            Resource: '*',
            Condition: OWN,
        },
        {
            // The cluster security group EKS created, which the EKS module tags.
            Sid: 'Ec2TagOwnClusterSecurityGroup',
            Effect: 'Allow',
            Action: ['ec2:CreateTags', 'ec2:DeleteTags'],
            Resource: arn('ec2', 'security-group/*'),
            Condition: { StringEquals: { 'aws:ResourceTag/aws:eks:cluster-name': CLUSTER } },
        },
        {
            /*
             * The VPC's own default security group, route table and network ACL,
             * which the VPC module adopts and tags. See
             * `DenyDeploymentTagOnNamedNetworkResources`: only while they carry no
             * tag of this deployment's or a `Name`.
             */
            // The default ACL's entries change before it is tagged; NACL_ENTRY_ACTIONS says why.
            Sid: 'Ec2EntriesOnAdoptedAclDefaults',
            Effect: 'Allow',
            Action: [...NACL_ENTRY_ACTIONS],
            Resource: arn('ec2', 'network-acl/*'),
            Condition: { Null: { 'aws:ResourceTag/qrvey-id': 'true', 'aws:ResourceTag/Name': 'true' } },
        },
        {
            Sid: 'Ec2TagAdoptedVpcDefaults',
            Effect: 'Allow',
            Action: 'ec2:CreateTags',
            Resource: [
                arn('ec2', 'security-group/*'),
                arn('ec2', 'route-table/*'),
                arn('ec2', 'network-acl/*'),
            ],
            Condition: { Null: { 'aws:ResourceTag/qrvey-id': 'true', 'aws:ResourceTag/Name': 'true' } },
        },
        {
            Sid: 'LoadBalancingAndScalingRead',
            Effect: 'Allow',
            Action: ['elasticloadbalancing:Describe*', 'autoscaling:Describe*'],
            Resource: '*',
        },
        {
            Sid: 'LoadBalancersOwn',
            Effect: 'Allow',
            Action: [
                'elasticloadbalancing:DeleteLoadBalancer',
                'elasticloadbalancing:DeleteTargetGroup',
                'elasticloadbalancing:DeleteListener',
            ],
            Resource: '*',
            Condition: OWN,
        },
        {
            /*
             * The destroy resumes a node group's scaling processes before deleting
             * it. The group is EKS's, and carries EKS's tag rather than ours.
             */
            Sid: 'ResumeOwnNodeGroupScaling',
            Effect: 'Allow',
            Action: 'autoscaling:ResumeProcesses',
            Resource: '*',
            Condition: { StringEquals: { 'aws:ResourceTag/eks:cluster-name': CLUSTER } },
        },
    ],
};
/**
 * What the destroy removes that this deployment's cluster made, one statement
 * per mark.
 *
 * One statement per mark because IAM ANDs the keys of a condition: the single
 * statement this replaced required a resource to carry **all** of
 * `CONTROLLER_SIGNALS` at once, which none does, so it allowed nothing. The
 * first scoped destroy (2026-09-29) was refused terminating a Karpenter node
 * that the rail had already let through. Each statement names only the
 * actions its controller's resources need.
 *
 * In `QrveyDeploy-Cluster`, whose cluster makes all of it. It sat in
 * `QrveyDeploy-IamPolicies` for size until IM-193 needed that room for
 * `IamSetOwnBoundary`; Compute is at its limit.
 */
const CONTROLLER_CLEANUP = [
    {
        // EKS's cluster security group, and the nodes EKS and Karpenter launch.
        Sid: 'CleanUpEksMarked',
        Effect: 'Allow',
        Action: [
            'ec2:TerminateInstances',
            'ec2:DeleteNetworkInterface',
            'ec2:DeleteSecurityGroup',
            'ec2:RevokeSecurityGroupIngress',
            'ec2:RevokeSecurityGroupEgress',
        ],
        Resource: '*',
        Condition: { StringEquals: { 'aws:ResourceTag/aws:eks:cluster-name': CLUSTER } },
    },
    {
        // Interfaces the VPC CNI left `available`.
        Sid: 'CleanUpCniMarked',
        Effect: 'Allow',
        Action: 'ec2:DeleteNetworkInterface',
        Resource: '*',
        Condition: { StringEquals: { 'aws:ResourceTag/cluster.k8s.amazonaws.com/name': CLUSTER } },
    },
    {
        // What the AWS Load Balancer Controller created.
        Sid: 'CleanUpLoadBalancerControllerMarked',
        Effect: 'Allow',
        Action: [
            'ec2:DeleteSecurityGroup',
            'ec2:RevokeSecurityGroupIngress',
            'ec2:RevokeSecurityGroupEgress',
            'elasticloadbalancing:DeleteLoadBalancer',
            'elasticloadbalancing:DeleteTargetGroup',
            'elasticloadbalancing:DeleteListener',
        ],
        Resource: '*',
        Condition: { StringEquals: { 'aws:ResourceTag/elbv2.k8s.aws/cluster': CLUSTER } },
    },
    {
        // Persistent volumes the EBS CSI driver provisioned.
        Sid: 'CleanUpCsiMarked',
        Effect: 'Allow',
        Action: 'ec2:DeleteVolume',
        Resource: '*',
        Condition: { StringEquals: { 'aws:ResourceTag/ebs.csi.aws.com/cluster-name': CLUSTER } },
    },
];
const cluster = {
    name: `${DEPLOY_POLICY_NAME_PREFIX}Cluster`,
    description: 'Qrvey deploy role: the EKS cluster, Aurora and its proxy, OpenSearch.',
    statements: [
        ...CONTROLLER_CLEANUP,
        {
            Sid: 'EksRead',
            Effect: 'Allow',
            Action: [
                'eks:DescribeAddonVersions',
                'eks:DescribeClusterVersions',
                'eks:ListClusters',
                'eks:ListAccessPolicies',
            ],
            Resource: '*',
        },
        {
            /*
             * One cluster, by its exact name. 24/7 left EKS on `*`; the name here is
             * `qrvey-eks-<id>` with nothing after it, so no other cluster matches.
             * Updates beyond the measurement are 24/7's, from its upgrade.
             */
            Sid: 'EksOwnCluster',
            Effect: 'Allow',
            Action: [
                'eks:CreateCluster',
                'eks:DeleteCluster',
                'eks:DescribeCluster',
                'eks:UpdateClusterConfig',
                'eks:UpdateClusterVersion',
                'eks:DescribeUpdate',
                'eks:ListUpdates',
                'eks:CreateNodegroup',
                'eks:DeleteNodegroup',
                'eks:DescribeNodegroup',
                'eks:ListNodegroups',
                'eks:UpdateNodegroupConfig',
                'eks:UpdateNodegroupVersion',
                'eks:CreateAddon',
                'eks:DeleteAddon',
                'eks:DescribeAddon',
                'eks:DescribeAddonConfiguration',
                'eks:ListAddons',
                'eks:UpdateAddon',
                'eks:CreateAccessEntry',
                'eks:DeleteAccessEntry',
                'eks:DescribeAccessEntry',
                'eks:ListAccessEntries',
                'eks:AssociateAccessPolicy',
                'eks:DisassociateAccessPolicy',
                'eks:ListAssociatedAccessPolicies',
                'eks:CreatePodIdentityAssociation',
                'eks:DeletePodIdentityAssociation',
                'eks:DescribePodIdentityAssociation',
                'eks:ListPodIdentityAssociations',
                'eks:TagResource',
                'eks:UntagResource',
                'eks:ListTagsForResource',
            ],
            Resource: [
                arn('eks', `cluster/${CLUSTER}`),
                arn('eks', `nodegroup/${CLUSTER}/*`),
                arn('eks', `addon/${CLUSTER}/*`),
                arn('eks', `access-entry/${CLUSTER}/*`),
                arn('eks', `podidentityassociation/${CLUSTER}/*`),
            ],
        },
        {
            Sid: 'RdsRead',
            Effect: 'Allow',
            Action: ['rds:Describe*', 'rds:ListTagsForResource'],
            Resource: '*',
        },
        {
            /*
             * Aurora, by the names the installer and `rds-aurora` 9.13.0 give it.
             * Parameter groups carry a timestamp suffix, hence `-*`. The default
             * option group is referenced by the create, not changed.
             */
            Sid: 'RdsOwnNames',
            Effect: 'Allow',
            Action: [
                'rds:CreateDBCluster',
                'rds:DeleteDBCluster',
                'rds:ModifyDBCluster',
                'rds:CreateDBInstance',
                'rds:DeleteDBInstance',
                'rds:ModifyDBInstance',
                'rds:CreateDBSubnetGroup',
                'rds:DeleteDBSubnetGroup',
                'rds:ModifyDBSubnetGroup',
                'rds:CreateDBClusterParameterGroup',
                'rds:DeleteDBClusterParameterGroup',
                'rds:ModifyDBClusterParameterGroup',
                'rds:CreateDBParameterGroup',
                'rds:DeleteDBParameterGroup',
                'rds:ModifyDBParameterGroup',
                'rds:RegisterDBProxyTargets',
                'rds:DeregisterDBProxyTargets',
                'rds:AddTagsToResource',
                'rds:RemoveTagsFromResource',
            ],
            Resource: [
                arn('rds', `cluster:qrvey-aurora-${ID}`),
                arn('rds', `db:qrvey-aurora-${ID}-*`),
                arn('rds', `subgrp:qrvey-aurora-${ID}`),
                arn('rds', `cluster-pg:qrvey-aurora-cluster-${ID}`),
                arn('rds', `cluster-pg:qrvey-aurora-cluster-${ID}-*`),
                arn('rds', `pg:qrvey-aurora-instance-${ID}`),
                arn('rds', `pg:qrvey-aurora-instance-${ID}-*`),
                arn('rds', 'og:default:*'),
            ],
        },
        {
            /*
             * The proxy has no name in its ARN (`db-proxy:prx-…`, which is what
             * blocked 24/7's first attempt), so it is held to the tag: created
             * tagged, and changed only while it carries this deployment's id.
             */
            Sid: 'RdsProxyCreateTagged',
            Effect: 'Allow',
            Action: ['rds:CreateDBProxy', 'rds:AddTagsToResource'],
            Resource: arn('rds', 'db-proxy:*'),
            Condition: TAGGED,
        },
        {
            // An endpoint is created on a proxy, which `RdsProxyManageOwn` requires
            // to be this deployment's; this statement is the new endpoint only.
            Sid: 'RdsProxyEndpointCreateTagged',
            Effect: 'Allow',
            Action: ['rds:CreateDBProxyEndpoint', 'rds:AddTagsToResource'],
            Resource: arn('rds', 'db-proxy-endpoint:*'),
            Condition: TAGGED,
        },
        {
            Sid: 'RdsProxyManageOwn',
            Effect: 'Allow',
            Action: [
                'rds:CreateDBProxyEndpoint',
                'rds:DeleteDBProxy',
                'rds:ModifyDBProxy',
                'rds:DeleteDBProxyEndpoint',
                'rds:ModifyDBProxyEndpoint',
                'rds:RegisterDBProxyTargets',
                'rds:DeregisterDBProxyTargets',
                'rds:ModifyDBProxyTargetGroup',
                'rds:RemoveTagsFromResource',
            ],
            Resource: [arn('rds', 'db-proxy:*'), arn('rds', 'db-proxy-endpoint:*')],
            Condition: OWN,
        },
        {
            /*
             * A proxy's target group cannot carry a tag, so `OWN` never matched it:
             * the second scoped apply (2026-09-29) was refused
             * `ModifyDBProxyTargetGroup` on `target-group:prx-tg-…` with "no
             * identity-based policy allows". Registering and deregistering also name
             * the proxy, and the proxy is still held to this deployment's tag above,
             * so those stay scoped. `ModifyDBProxyTargetGroup` names the target group
             * alone, which makes it the residual: a run could change the connection
             * pool settings of another proxy's default target group. No data, and
             * no target, moves by it.
             */
            Sid: 'RdsProxyTargetGroups',
            Effect: 'Allow',
            Action: [
                'rds:RegisterDBProxyTargets',
                'rds:DeregisterDBProxyTargets',
                'rds:ModifyDBProxyTargetGroup',
            ],
            Resource: arn('rds', 'target-group:*'),
        },
        {
            Sid: 'OpenSearchRead',
            Effect: 'Allow',
            Action: [
                'es:DescribeDomain*',
                'es:ListDomainNames',
                'es:ListTags',
                'es:GetCompatibleVersions',
            ],
            Resource: '*',
        },
        {
            // `qrvey-<id>-vpc`, exactly. No data-plane access: nothing measured used it.
            Sid: 'OpenSearchOwnDomain',
            Effect: 'Allow',
            Action: [
                'es:CreateDomain',
                'es:DeleteDomain',
                'es:UpdateDomainConfig',
                'es:UpgradeDomain',
                'es:AddTags',
                'es:RemoveTags',
            ],
            Resource: arn('es', `domain/qrvey-${ID}-vpc`),
        },
    ],
};
const data = {
    name: `${DEPLOY_POLICY_NAME_PREFIX}Data`,
    description: 'Qrvey deploy role: KMS, secrets, Athena and Glue, logs and events.',
    statements: [
        {
            Sid: 'KmsRead',
            Effect: 'Allow',
            Action: [
                'kms:DescribeKey',
                'kms:GetKeyPolicy',
                'kms:GetKeyRotationStatus',
                'kms:ListAliases',
                'kms:ListKeys',
                'kms:ListResourceTags',
                'kms:ListGrants',
            ],
            Resource: '*',
        },
        {
            /*
             * A key may be created only tagged. `kms:TagResource` rides with it
             * because KMS checks it when `CreateKey` carries tags — and that is the
             * non-EC2 gap in the module comment: the same permission lets a run tag
             * an existing untagged key.
             */
            Sid: 'KmsCreateTagged',
            Effect: 'Allow',
            Action: ['kms:CreateKey', 'kms:TagResource'],
            Resource: '*',
            Condition: TAGGED,
        },
        {
            Sid: 'KmsManageOwnKeys',
            Effect: 'Allow',
            Action: [
                'kms:ScheduleKeyDeletion',
                'kms:EnableKeyRotation',
                'kms:PutKeyPolicy',
                'kms:UpdateKeyDescription',
                'kms:TagResource',
                'kms:UntagResource',
                'kms:CreateGrant',
                'kms:RevokeGrant',
                'kms:RetireGrant',
                'kms:CreateAlias',
                'kms:DeleteAlias',
                'kms:UpdateAlias',
                'kms:Encrypt',
                'kms:Decrypt',
                'kms:GenerateDataKey*',
                'kms:ReEncrypt*',
            ],
            Resource: '*',
            Condition: OWN,
        },
        {
            // An alias is authorized on the alias and on its key: this is the alias half.
            Sid: 'KmsOwnAliases',
            Effect: 'Allow',
            Action: ['kms:CreateAlias', 'kms:DeleteAlias', 'kms:UpdateAlias'],
            Resource: arn('kms', `alias/eks/${CLUSTER}`),
        },
        {
            Sid: 'KmsThroughServices',
            Effect: 'Allow',
            Action: ['kms:Encrypt', 'kms:Decrypt', 'kms:GenerateDataKey*', 'kms:ReEncrypt*'],
            Resource: '*',
            Condition: { StringLike: { 'kms:ViaService': VIA_SERVICES } },
        },
        {
            Sid: 'KmsGrantsForAwsServices',
            Effect: 'Allow',
            Action: 'kms:CreateGrant',
            Resource: '*',
            Condition: { Bool: { 'kms:GrantIsForAWSResource': 'true' } },
        },
        {
            // `qrvey-rds-<id>-<random>`. The only data read of a secret, and of its own.
            Sid: 'SecretsOwn',
            Effect: 'Allow',
            Action: [
                'secretsmanager:CreateSecret',
                'secretsmanager:DeleteSecret',
                'secretsmanager:DescribeSecret',
                'secretsmanager:GetResourcePolicy',
                'secretsmanager:GetSecretValue',
                'secretsmanager:PutSecretValue',
                'secretsmanager:UpdateSecret',
                'secretsmanager:TagResource',
                'secretsmanager:UntagResource',
            ],
            Resource: arn('secretsmanager', `secret:qrvey-rds-${ID}-*`),
        },
        {
            Sid: 'AthenaCatalogRead',
            Effect: 'Allow',
            Action: ['athena:GetDatabase', 'athena:GetDataCatalog', 'athena:ListDatabases'],
            Resource: '*',
        },
        {
            Sid: 'AthenaOwnWorkgroups',
            Effect: 'Allow',
            Action: [
                'athena:CreateWorkGroup',
                'athena:DeleteWorkGroup',
                'athena:GetWorkGroup',
                'athena:UpdateWorkGroup',
                'athena:TagResource',
                'athena:UntagResource',
                'athena:ListTagsForResource',
                'athena:StartQueryExecution',
                'athena:GetQueryExecution',
                'athena:GetQueryResults',
            ],
            Resource: arn('athena', `workgroup/${ID}_*`),
        },
        {
            /*
             * `aws_athena_database` creates and drops its database with a query in
             * the account's `primary` workgroup. What the query can touch is decided
             * by the Glue and S3 statements, which are this deployment's only.
             */
            Sid: 'AthenaDatabaseQueries',
            Effect: 'Allow',
            Action: ['athena:StartQueryExecution', 'athena:GetQueryExecution', 'athena:GetQueryResults'],
            Resource: arn('athena', 'workgroup/primary'),
        },
        {
            // `qrvey_<id>`, exactly, and its tables.
            Sid: 'GlueOwnDatabase',
            Effect: 'Allow',
            Action: [
                'glue:CreateDatabase',
                'glue:DeleteDatabase',
                'glue:GetDatabase',
                'glue:GetTables',
                'glue:GetTable',
                'glue:DeleteTable',
                'glue:TagResource',
            ],
            Resource: [
                arn('glue', 'catalog'),
                arn('glue', `database/qrvey_${ID}`),
                arn('glue', `table/qrvey_${ID}/*`),
                /*
                 * Dropping a database is authorized against its functions too, even
                 * when it has none: the second scoped destroy (2026-09-29) was refused
                 * `glue:DeleteDatabase` on `userDefinedFunction/qrvey_<id>/*`.
                 */
                arn('glue', `userDefinedFunction/qrvey_${ID}/*`),
            ],
        },
        {
            Sid: 'LogsRead',
            Effect: 'Allow',
            Action: ['logs:DescribeLogGroups'],
            Resource: '*',
        },
        {
            /*
             * The log groups the EKS, proxy and Aurora modules create, by their exact
             * paths. The customer's `/qrvey/terraform-runs` matches none of these.
             */
            Sid: 'LogsOwnGroups',
            Effect: 'Allow',
            Action: [
                'logs:CreateLogGroup',
                'logs:DeleteLogGroup',
                'logs:PutRetentionPolicy',
                'logs:DeleteRetentionPolicy',
                'logs:TagResource',
                'logs:UntagResource',
                'logs:TagLogGroup',
                'logs:ListTagsForResource',
                'logs:ListTagsLogGroup',
            ],
            Resource: [
                arn('logs', `log-group:/aws/eks/${CLUSTER}/*`),
                arn('logs', `log-group:/aws/rds/proxy/qrvey-rds-proxy-${ID}`),
                arn('logs', `log-group:/aws/rds/proxy/qrvey-rds-proxy-${ID}:*`),
                arn('logs', `log-group:/aws/rds/cluster/qrvey-aurora-${ID}/*`),
                arn('logs', `log-group:/aws/containerinsights/${CLUSTER}/*`),
            ],
        },
        {
            Sid: 'EventRulesRead',
            Effect: 'Allow',
            Action: [
                'events:DescribeRule',
                'events:ListRules',
                'events:ListTargetsByRule',
                'events:ListTagsForResource',
            ],
            Resource: '*',
        },
        {
            /*
             * Karpenter's interruption rules are `Karpenter<Event>-<timestamp>`: no
             * deployment id. Held to the tag instead — created tagged, changed only
             * while this deployment's.
             */
            Sid: 'KarpenterRulesCreateTagged',
            Effect: 'Allow',
            Action: ['events:PutRule', 'events:TagResource'],
            Resource: arn('events', 'rule/Karpenter*'),
            Condition: TAGGED,
        },
        {
            Sid: 'KarpenterRulesManageOwn',
            Effect: 'Allow',
            Action: [
                'events:PutRule',
                'events:DeleteRule',
                'events:PutTargets',
                'events:RemoveTargets',
                'events:EnableRule',
                'events:DisableRule',
                'events:TagResource',
                'events:UntagResource',
            ],
            Resource: arn('events', 'rule/Karpenter*'),
            Condition: OWN,
        },
    ],
};
const state = {
    name: `${DEPLOY_POLICY_NAME_PREFIX}Storage`,
    description: 'Qrvey deploy role: its Terraform state, its buckets, parameters, topics and queue.',
    statements: [
        {
            Sid: 'ListBuckets',
            Effect: 'Allow',
            Action: 's3:ListAllMyBuckets',
            Resource: '*',
        },
        {
            /*
             * The region stack's state bucket, which every deployment in a region
             * shares. Listing it shows object names, and those are deployment ids —
             * already readable from every tag through `ec2:Describe*`. It is also what
             * the wrapper's `head-bucket` ownership check needs.
             */
            Sid: 'StateBucket',
            Effect: 'Allow',
            Action: ['s3:ListBucket', 's3:GetBucketLocation', 's3:GetBucketVersioning'],
            Resource: s3(`qrvey-tfstate-${A}-*`),
        },
        {
            /*
             * This deployment's state, and no other's: `<id>/state.tfstate` is the
             * key the wrapper derives and the one the control plane records for every
             * instance it creates (IM-139). An adopted instance whose state is
             * elsewhere is the `qrveyversion-<id>` bucket below, or it is refused.
             */
            Sid: 'OwnStateObjects',
            Effect: 'Allow',
            Action: ['s3:GetObject', 's3:PutObject', 's3:DeleteObject'],
            Resource: s3(`qrvey-tfstate-${A}-*/${ID}/*`),
        },
        {
            /*
             * Terraform's lock, keyed `<bucket>/<key>` and `<bucket>/<key>-md5`.
             * `dynamodb:LeadingKeys` is the partition key, which is exactly that
             * string, so this deployment's lock items are the only ones it can touch.
             */
            Sid: 'OwnStateLock',
            Effect: 'Allow',
            Action: ['dynamodb:GetItem', 'dynamodb:PutItem', 'dynamodb:DeleteItem'],
            Resource: arn('dynamodb', 'table/qrvey-terraform-locks'),
            Condition: {
                'ForAllValues:StringLike': {
                    'dynamodb:LeadingKeys': [`qrvey-tfstate-${A}-*/${ID}/*`, `qrveyversion-${ID}/*`],
                },
                Null: { 'dynamodb:LeadingKeys': 'false' },
            },
        },
        {
            /*
             * The installer's own buckets, by the names it gives them — including
             * emptying them on destroy, which is data events no trace can show.
             * `qrveyversion-<id>` is the manual flow's state bucket, for an adopted
             * instance.
             */
            Sid: 'OwnBuckets',
            Effect: 'Allow',
            Action: [
                's3:CreateBucket',
                's3:DeleteBucket',
                's3:Get*',
                's3:List*',
                's3:PutBucketPolicy',
                's3:DeleteBucketPolicy',
                's3:PutBucketTagging',
                's3:PutBucketVersioning',
                's3:PutBucketPublicAccessBlock',
                's3:PutBucketCORS',
                's3:PutLifecycleConfiguration',
                's3:PutEncryptionConfiguration',
                's3:PutBucketOwnershipControls',
                's3:PutObject',
                's3:DeleteObject',
                's3:DeleteObjectVersion',
                's3:AbortMultipartUpload',
            ],
            Resource: [
                s3(`qrvey-${ID}-*`),
                s3(`qrvey-${ID}-*/*`),
                s3(`${ID}-dataload-*`),
                s3(`${ID}-dataload-*/*`),
                s3(`${ID}-qrveyuserfiles`),
                s3(`${ID}-qrveyuserfiles/*`),
                s3(`qrveyversion-${ID}`),
                s3(`qrveyversion-${ID}/*`),
            ],
        },
        {
            Sid: 'ParametersRead',
            Effect: 'Allow',
            Action: 'ssm:DescribeParameters',
            Resource: '*',
        },
        {
            // AWS's public parameters, such as the EKS AMI ids. Not customer data.
            Sid: 'PublicParameters',
            Effect: 'Allow',
            Action: ['ssm:GetParameter', 'ssm:GetParameters'],
            Resource: `arn:${P}:ssm:*::parameter/aws/service/*`,
        },
        {
            /*
             * `/qrvey/<id>/…` and the two paths outside it the installer writes. Not
             * `/qrvey/*`, which 24/7 allowed: that is also where the region stack
             * publishes `/qrvey/bootstrap/version` and `/qrvey/region/version`.
             */
            Sid: 'OwnParameters',
            Effect: 'Allow',
            Action: [
                'ssm:GetParameter',
                'ssm:GetParameters',
                'ssm:PutParameter',
                'ssm:DeleteParameter',
                'ssm:AddTagsToResource',
                'ssm:RemoveTagsFromResource',
                'ssm:ListTagsForResource',
            ],
            Resource: [
                arn('ssm', `parameter/qrvey/${ID}/*`),
                arn('ssm', `parameter/${ID}/*`),
                arn('ssm', `parameter/sun/privateapikey/${ID}`),
            ],
        },
        {
            Sid: 'OwnTopics',
            Effect: 'Allow',
            Action: [
                'sns:CreateTopic',
                'sns:DeleteTopic',
                'sns:GetTopicAttributes',
                'sns:SetTopicAttributes',
                'sns:ListTagsForResource',
                'sns:TagResource',
                'sns:UntagResource',
            ],
            Resource: [arn('sns', `${ID}-datasync-*`), arn('sns', `${ID}-dataload-*`)],
        },
        {
            /*
             * Karpenter's interruption queue. The reads are not in the trace — SQS
             * does not record them — and Terraform makes them on every refresh.
             */
            Sid: 'OwnQueue',
            Effect: 'Allow',
            Action: [
                'sqs:CreateQueue',
                'sqs:DeleteQueue',
                'sqs:SetQueueAttributes',
                'sqs:GetQueueAttributes',
                'sqs:GetQueueUrl',
                'sqs:ListQueueTags',
                'sqs:TagQueue',
                'sqs:UntagQueue',
            ],
            Resource: arn('sqs', `Karpenter-${CLUSTER}`),
        },
        {
            /*
             * The user-files distribution, which has no name in its ARN: held to the
             * tag. Its origin access identity cannot carry one, and is the residual.
             */
            Sid: 'CloudFrontRead',
            Effect: 'Allow',
            Action: [
                'cloudfront:GetDistribution',
                'cloudfront:GetDistributionConfig',
                'cloudfront:ListDistributions',
                'cloudfront:ListTagsForResource',
                'cloudfront:GetCloudFrontOriginAccessIdentity',
                'cloudfront:GetCloudFrontOriginAccessIdentityConfig',
                'cloudfront:ListCloudFrontOriginAccessIdentities',
            ],
            Resource: '*',
        },
        {
            Sid: 'CloudFrontCreateTagged',
            Effect: 'Allow',
            Action: ['cloudfront:CreateDistribution', 'cloudfront:TagResource'],
            Resource: `arn:${P}:cloudfront::${A}:distribution/*`,
            Condition: TAGGED,
        },
        {
            Sid: 'CloudFrontManageOwn',
            Effect: 'Allow',
            Action: [
                'cloudfront:UpdateDistribution',
                'cloudfront:DeleteDistribution',
                'cloudfront:TagResource',
                'cloudfront:UntagResource',
            ],
            Resource: `arn:${P}:cloudfront::${A}:distribution/*`,
            Condition: OWN,
        },
        {
            Sid: 'CloudFrontOriginAccessIdentity',
            Effect: 'Allow',
            Action: [
                'cloudfront:CreateCloudFrontOriginAccessIdentity',
                'cloudfront:DeleteCloudFrontOriginAccessIdentity',
            ],
            Resource: `arn:${P}:cloudfront::${A}:origin-access-identity/*`,
        },
        {
            /*
             * Reads the Terraform provider and the installer make that concern no
             * resource: who am I, public registry auth for the Karpenter chart, one
             * quota, the hosted zones, Amazon Location's two describes.
             * `sts:GetServiceBearerToken` is not in the trace; public ECR
             * authorization needs it beside `ecr-public:GetAuthorizationToken`.
             */
            Sid: 'AccountLevelReads',
            Effect: 'Allow',
            Action: [
                'sts:GetCallerIdentity',
                'sts:GetServiceBearerToken',
                'ecr-public:GetAuthorizationToken',
                'servicequotas:GetServiceQuota',
                'route53:ListHostedZones',
                'route53:ListHostedZonesByName',
                'geo:DescribeMap',
                'geo:DescribePlaceIndex',
            ],
            Resource: '*',
        },
        {
            /*
             * Amazon Location's maps and place index (IM-190). Their names carry no
             * deployment id — `Esri.*` and `Qrvey_LocationIndex`, fixed per account
             * and region, which is an image finding of its own — so they are held to
             * the tag: created carrying this deployment's id, and changed or deleted
             * only while they do. Measured missing on the first scoped destroy of an
             * instance with location services on (2026-10-08), refused `DeleteMap`
             * and `DeletePlaceIndex`; a scoped apply would have been refused at the
             * create.
             */
            Sid: 'LocationCreateTagged',
            Effect: 'Allow',
            Action: ['geo:CreateMap', 'geo:CreatePlaceIndex', 'geo:TagResource'],
            Resource: [arn('geo', 'map/Esri.*'), arn('geo', 'place-index/Qrvey_LocationIndex')],
            Condition: TAGGED,
        },
        {
            Sid: 'LocationManageOwn',
            Effect: 'Allow',
            Action: [
                'geo:DeleteMap',
                'geo:UpdateMap',
                'geo:DeletePlaceIndex',
                'geo:UpdatePlaceIndex',
                'geo:TagResource',
                'geo:UntagResource',
            ],
            Resource: [arn('geo', 'map/Esri.*'), arn('geo', 'place-index/Qrvey_LocationIndex')],
            Condition: OWN,
        },
    ],
};
/**
 * The IAM role statements, with the permissions boundary requirement left open.
 *
 * `boundary` is how the requirement is expressed where it applies: absent, the
 * statements are what they are; present, `CreateRole`, `AttachRolePolicy` and
 * `PutRolePolicy` additionally require `iam:PermissionsBoundary` to equal it.
 * The template renders both and chooses with `Fn::If`, because whether the
 * image sets a boundary (MP-3619) is not known here.
 *
 * Its own policy, apart from the rest of IAM, because these are the three
 * statements a boundary applies to — and because ten role patterns repeated
 * across six statements do not fit in one policy's 6,144 characters.
 */
export const iamRoleStatements = (boundary) => {
    const bounded = boundary === undefined ? {} : { 'iam:PermissionsBoundary': boundary };
    return [
        {
            Sid: 'IamCreateRoleTagged',
            Effect: 'Allow',
            Action: 'iam:CreateRole',
            Resource: [...ROLE_PATTERNS, PATH_ROLES],
            Condition: { StringEquals: { ...TAGGED.StringEquals, ...bounded } },
        },
        {
            /*
             * Only the policies the installer attaches: its own, and a short list of
             * AWS-managed ones. Not `AdministratorAccess`, and not anything else.
             */
            Sid: 'IamAttachKnownPolicies',
            Effect: 'Allow',
            Action: 'iam:AttachRolePolicy',
            Resource: [...ROLE_PATTERNS, PATH_ROLES],
            Condition: {
                StringEquals: { ...OWN.StringEquals, ...bounded },
                ArnLike: {
                    'iam:PolicyARN': [...ATTACHABLE_POLICY_ARNS],
                },
            },
        },
        {
            /*
             * An inline policy's content cannot be constrained by any condition, so
             * this is where the role remains able to escalate until a boundary is
             * required: see `DeployRoleBoundaryArn`.
             */
            Sid: 'IamPutInlinePolicy',
            Effect: 'Allow',
            Action: 'iam:PutRolePolicy',
            Resource: [...ROLE_PATTERNS, PATH_ROLES],
            Condition: { StringEquals: { ...OWN.StringEquals, ...bounded } },
        },
    ];
};
/**
 * Using the roles once they exist: tagging on create, deleting, passing.
 * None of these can carry a boundary condition, so they sit apart from the
 * three that do.
 */
const iamRoleUse = {
    name: `${DEPLOY_POLICY_NAME_PREFIX}IamRoleUse`,
    description: 'Qrvey deploy role: tagging, changing, deleting and passing its own IAM roles.',
    statements: [
        {
            /*
             * Tag-on-create for a role, which IAM authorizes as a separate `TagRole`
             * against a role that has no tags yet. The non-EC2 gap applies: an
             * untagged role whose name matches a pattern can be claimed.
             *
             * `PutRolePermissionsBoundary` rides here in case IAM authorizes a
             * `CreateRole` that names a boundary as that action too, as a new
             * deployment's roles do from MP-3693 — where the role has no tags yet
             * for `IamSetOwnBoundary` to read. Unmeasured. Outside a create no
             * request carries a tag, so this allows nothing; and if IAM does ask, a
             * create naming any other boundary meets `DenyOtherBoundaries`.
             */
            Sid: 'IamTagRoleOnCreate',
            Effect: 'Allow',
            Action: ['iam:TagRole', 'iam:PutRolePermissionsBoundary'],
            Resource: [...ROLE_PATTERNS, PATH_ROLES],
            Condition: TAGGED,
        },
        {
            Sid: 'IamManageOwnRoles',
            Effect: 'Allow',
            Action: [
                'iam:DeleteRole',
                'iam:DeleteRolePolicy',
                'iam:DetachRolePolicy',
                'iam:UpdateRole',
                'iam:UpdateRoleDescription',
                'iam:TagRole',
                'iam:UntagRole',
            ],
            Resource: [...ROLE_PATTERNS, PATH_ROLES],
            Condition: OWN,
        },
        {
            /*
             * Passed by name, not by tag. The first scoped apply (2026-09-29) was
             * refused `iam:PassRole` on `qrvey-eks-<id>-cluster-…` and on
             * `qrvey-rds-proxy-<id>` — both carrying this deployment's `qrvey-id` —
             * with "no identity-based policy allows": PassRole is not evaluated
             * against `aws:ResourceTag`, though the policy simulator happily is. The
             * name is no weaker a scope here, because every one of these patterns
             * embeds the session's own id and `IamCreateRoleTagged` is what creates
             * them.
             */
            Sid: 'IamPassOwnRoles',
            Effect: 'Allow',
            Action: 'iam:PassRole',
            Resource: [...ROLE_PATTERNS, ...PATH_ROLE_PATTERNS].filter((pattern) => pattern.includes(ID)),
            Condition: { StringEquals: { 'iam:PassedToService': [...PASSED_TO_SERVICES] } },
        },
        {
            /*
             * The two prefix-only patterns carry no id, so they keep a tag — as
             * `iam:ResourceTag`, IAM's own key for a role's tags. Unmeasured: if
             * PassRole ignores this key too, a node group's role is refused and the
             * apply says so.
             */
            Sid: 'IamPassOwnPrefixRoles',
            Effect: 'Allow',
            Action: 'iam:PassRole',
            Resource: [...ROLE_PATTERNS, ...PATH_ROLE_PATTERNS].filter((pattern) => !pattern.includes(ID)),
            Condition: {
                StringEquals: {
                    'iam:ResourceTag/qrvey-id': ID,
                    'iam:PassedToService': [...PASSED_TO_SERVICES],
                },
            },
        },
    ],
};
/** Everything else in IAM: reads, policies, instance profiles, the OIDC provider. */
const iamOther = {
    name: `${DEPLOY_POLICY_NAME_PREFIX}IamPolicies`,
    // Never edit a description: CloudFormation replaces a managed policy whose
    // description changes, and the replacement collides with the fixed name —
    // measured, as a rolled-back access stack update on 2026-09-29.
    description: 'Qrvey deploy role: the IAM policies, instance profiles and identity provider.',
    statements: [
        {
            /*
             * This deployment's own boundary on its own roles, and no other (IM-193).
             * Every boundary is named for its deployment, at the root path for `/`
             * and `/qrvey/` deployments alike (MP-3693), so one condition with the
             * session tag says "its own". It is how a boundary reaches a deployment
             * that existed before it had one (MP-3673's in-place apply). Requiring
             * one on `CreateRole` is the account-wide `required` mode, IM-194.
             *
             * Here rather than beside the other role writes for size alone: this
             * statement is the only place this policy names `ROLE_PATTERNS`, while
             * `IamRoleUse` repeats each pattern four times.
             */
            Sid: 'IamSetOwnBoundary',
            Effect: 'Allow',
            Action: 'iam:PutRolePermissionsBoundary',
            Resource: [...ROLE_PATTERNS, PATH_ROLES],
            Condition: { StringEquals: { ...OWN.StringEquals, 'iam:PermissionsBoundary': OWN_BOUNDARY } },
        },
        {
            Sid: 'IamRead',
            Effect: 'Allow',
            Action: [
                'iam:GetRole',
                'iam:GetRolePolicy',
                'iam:GetPolicy',
                'iam:GetPolicyVersion',
                'iam:GetInstanceProfile',
                'iam:GetOpenIDConnectProvider',
                'iam:ListRoles',
                'iam:ListPolicies',
                'iam:ListPolicyVersions',
                'iam:ListAttachedRolePolicies',
                'iam:ListRolePolicies',
                'iam:ListRoleTags',
                'iam:ListPolicyTags',
                'iam:ListInstanceProfiles',
                'iam:ListInstanceProfilesForRole',
                'iam:ListEntitiesForPolicy',
                'iam:ListOpenIDConnectProviderTags',
            ],
            Resource: '*',
        },
        {
            Sid: 'IamCreatePolicyTagged',
            Effect: 'Allow',
            Action: ['iam:CreatePolicy', 'iam:TagPolicy'],
            Resource: [...POLICY_PATTERNS, PATH_POLICIES],
            Condition: TAGGED,
        },
        {
            Sid: 'IamManageOwnPolicies',
            Effect: 'Allow',
            Action: [
                'iam:CreatePolicyVersion',
                'iam:DeletePolicyVersion',
                'iam:DeletePolicy',
                'iam:TagPolicy',
                'iam:UntagPolicy',
            ],
            Resource: [...POLICY_PATTERNS, PATH_POLICIES],
            Condition: OWN,
        },
        {
            /*
             * Karpenter's instance profiles, which Karpenter creates under
             * `/karpenter/<region>/<cluster>/` and the destroy has to empty and
             * remove — `DeleteInstanceProfile` is one of the three deletes the
             * measured destroy should have made and did not (IM-128).
             */
            Sid: 'IamOwnInstanceProfiles',
            Effect: 'Allow',
            Action: ['iam:RemoveRoleFromInstanceProfile', 'iam:DeleteInstanceProfile'],
            Resource: iam(`instance-profile/karpenter/*/${CLUSTER}/*`),
        },
        {
            /*
             * Karpenter's node instance profile, which a deployment under the
             * installer's path has Terraform create (MP-3619) rather than leaving
             * Karpenter to make one per node class at runtime. Created only carrying
             * this deployment's tag, and changed only while it does.
             */
            Sid: 'IamPathInstanceProfileTagged',
            Effect: 'Allow',
            Action: ['iam:CreateInstanceProfile', 'iam:TagInstanceProfile'],
            Resource: iam(`instance-profile/${UNDER_PATH}*`),
            Condition: TAGGED,
        },
        {
            Sid: 'IamPathInstanceProfileOwn',
            Effect: 'Allow',
            Action: [
                'iam:AddRoleToInstanceProfile',
                'iam:RemoveRoleFromInstanceProfile',
                'iam:DeleteInstanceProfile',
                'iam:TagInstanceProfile',
                'iam:UntagInstanceProfile',
            ],
            Resource: iam(`instance-profile/${UNDER_PATH}*`),
            Condition: OWN,
        },
        {
            /*
             * The cluster's OIDC provider. Its ARN is the issuer URL, which names
             * no deployment, so it is held to the tag.
             */
            Sid: 'IamOidcProviderTagged',
            Effect: 'Allow',
            Action: ['iam:CreateOpenIDConnectProvider', 'iam:TagOpenIDConnectProvider'],
            Resource: iam('oidc-provider/oidc.eks.*'),
            Condition: TAGGED,
        },
        {
            Sid: 'IamOidcProviderManageOwn',
            Effect: 'Allow',
            Action: [
                'iam:DeleteOpenIDConnectProvider',
                'iam:UpdateOpenIDConnectProviderThumbprint',
                'iam:TagOpenIDConnectProvider',
                'iam:UntagOpenIDConnectProvider',
            ],
            Resource: iam('oidc-provider/oidc.eks.*'),
            Condition: OWN,
        },
        {
            // 24/7's list. Each is a role AWS defines; none can be given a policy.
            Sid: 'IamServiceLinkedRoles',
            Effect: 'Allow',
            Action: 'iam:CreateServiceLinkedRole',
            Resource: iam('role/aws-service-role/*'),
            Condition: {
                StringEquals: {
                    'iam:AWSServiceName': [
                        'eks.amazonaws.com',
                        'eks-nodegroup.amazonaws.com',
                        'elasticloadbalancing.amazonaws.com',
                        'es.amazonaws.com',
                        'opensearchservice.amazonaws.com',
                        'rds.amazonaws.com',
                        'spot.amazonaws.com',
                        'spotfleet.amazonaws.com',
                    ],
                },
            },
        },
    ],
};
/** The role policy's name, which the template builds itself so it can require a boundary. */
export const IAM_ROLES_POLICY_NAME = `${DEPLOY_POLICY_NAME_PREFIX}IamRoles`;
export const IAM_ROLES_POLICY_DESCRIPTION = 'Qrvey deploy role: the IAM roles the installer creates, and what it may attach to them.';
/**
 * The whole set, in the order the template attaches it, as it applies when no
 * boundary is required.
 */
export const DEPLOY_POLICIES = [
    guardrails,
    iamGuardrails,
    compute,
    cluster,
    data,
    state,
    {
        name: IAM_ROLES_POLICY_NAME,
        description: IAM_ROLES_POLICY_DESCRIPTION,
        statements: iamRoleStatements(),
    },
    iamRoleUse,
    iamOther,
];
/** IAM's limit on a managed policy document, counted without whitespace. */
export const MANAGED_POLICY_SIZE_LIMIT = 6144;
/** The document for one policy, in IAM's own form. */
export const policyDocument = (statements) => ({
    Version: '2012-10-17',
    Statement: statements,
});
/**
 * A value with CloudFormation's tokens resolved for one account, as IAM will
 * store it — for the size check, the simulator and the customer document.
 */
export const forAccount = (value, partition, accountId) => JSON.parse(JSON.stringify(value)
    .replaceAll('${AWS::Partition}', partition)
    .replaceAll('${AWS::AccountId}', accountId));
/**
 * A value as CloudFormation must be given it.
 *
 * A string holding a CloudFormation token becomes `Fn::Sub`, with every IAM
 * variable escaped as `${!…}` — without that, `Fn::Sub` would try to resolve
 * `${aws:PrincipalTag/qrvey-id}` itself and the stack would fail to create, or
 * worse, render an empty string where the deployment id belongs. A string with
 * no token is left alone, variables included, because nothing substitutes it.
 */
export const forCloudFormation = (value) => {
    if (typeof value === 'string') {
        return value.includes('${AWS::') ? { 'Fn::Sub': value.replaceAll('${aws:', '${!aws:') } : value;
    }
    if (Array.isArray(value))
        return value.map(forCloudFormation);
    if (value !== null && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, forCloudFormation(v)]));
    }
    return value;
};
/** The two forms of the IAM policy the template chooses between. */
export const iamPolicyVariants = (boundaryRef) => ({
    unbounded: forCloudFormation(policyDocument(iamRoleStatements())),
    bounded: substituteBoundary(forCloudFormation(policyDocument(iamRoleStatements(BOUNDARY_PLACEHOLDER))), boundaryRef),
});
const BOUNDARY_PLACEHOLDER = '__QRVEY_BOUNDARY__';
const substituteBoundary = (value, replacement) => {
    if (value === BOUNDARY_PLACEHOLDER)
        return replacement;
    if (Array.isArray(value))
        return value.map((v) => substituteBoundary(v, replacement));
    if (value !== null && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, substituteBoundary(v, replacement)]));
    }
    return value;
};
//# sourceMappingURL=deploy-policy.js.map